import {
  IMMUTABLE_LOGO,
  RedirectLayout,
} from "@/components/core/RedirectLayout";
import { SOCIAL_LINK_ERROR_MESSAGES, appConfig } from "@/constants";
import {
  Action,
  UserJourney,
  useLoginAnalytics,
} from "@/context/AnalyticsProvider";
import { useAccessToken } from "@/hooks";
import {
  type LinkedAccountPlatform,
  PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY,
  type PendingSocialQuest,
} from "@/utils/linkedaccounts/linkedAccounts";
import { X_MEDIA_UPLOAD_STORAGE_KEY } from "@/utils/linkedaccounts/x";
import { notifyError } from "@/utils/monitoring";
import { trackLegacyEvent } from "@analytics";
import { usePrevious } from "@biom3/react";
import type { QuestKey } from "@ui-kit/QuestList";
import { AxiosError } from "axios";
import { usePathname } from "next/navigation";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getCustomErrorContent } from "./FollowConfirmationModal";

const base64ToFile = (base64String: string, filename: string): File => {
  const base64Data = base64String.split(",")[1];
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const mimeType = base64String.match(/data:([^;]+)/)?.[1] || "image/jpeg";

  return new File([byteArray], filename, { type: mimeType });
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = () => {
      reject(new Error("Failed to convert image to base64"));
    };
    reader.readAsDataURL(file);
  });
};

const extractSteamIdFromParams = (params: string): string | null => {
  try {
    const urlParams = new URLSearchParams(params);
    const claimedId = urlParams.get("openid.claimed_id");

    if (claimedId) {
      // Steam OpenID returns URLs like: https://steamcommunity.com/openid/id/123456789
      // We need to extract the numeric Steam ID (123456789) from the end
      const match = claimedId.match(/\/id\/(\d+)$/);
      return match ? match[1] : null;
    }

    return null;
  } catch (error) {
    console.error("Failed to extract Steam ID from params:", error);
    return null;
  }
};

export default function LinkedAccountLogin({
  platform,
  errorName,
  completeQuestOnSuccess,
}: {
  platform: LinkedAccountPlatform;
  errorName: "xLogin" | "discordLogin" | "steamLogin";
  completeQuestOnSuccess: boolean;
}) {
  const getAccessToken = useAccessToken();
  const [title, setTitle] = useState<string>(
    `Connecting to ${platform.name}...`,
  );
  const [message, setMessage] = useState<ReactNode>("Hang tight");
  const [callbackParams, setCallbackParams] = useState<string | null>(null);
  const previousCallbackParams = usePrevious(callbackParams);
  const triggeredLoggedIn = useRef(false);
  const [following, setFollowing] = useState<boolean>(false);

  const { track: trackLogin } = useLoginAnalytics();
  const pathname = usePathname();

  const completeQuest = useCallback(
    async (
      gameId: string,
      questId: string,
      gameName: string,
      questKey: QuestKey,
      tweetText?: string,
    ) => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error("Unable to retrieve access token.");
          setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
          return;
        }

        let following: boolean;

        let completionTitle: string;

        switch (questKey) {
          case "x-post-tweet-validation":
            completionTitle = `You've posted a tweet for ${gameName} on ${platform.name}`;
            break;
          case "x-v2": {
            const pendingSocialQuest: PendingSocialQuest | null = JSON.parse(
              localStorage.getItem(PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY) ??
                "{}",
            );
            const twitterHandle = pendingSocialQuest?.twitterHandle || gameName;
            completionTitle = `You're now following ${twitterHandle} on ${platform.name}`;
            break;
          }
          case "x-like-and-retweet-validation":
            completionTitle = `You've liked and retweeted a post for ${gameName} on ${platform.name}`;
            break;
          case "steam":
            completionTitle = `You've wishlisted ${gameName} on ${platform.name}`;
            break;
          default:
            completionTitle = `You've completed a quest for ${gameName} on ${platform.name}`;
        }

        if (
          questKey === "x-post-tweet-validation" &&
          platform.validatePostTweetQuest &&
          platform.uploadXImageFile
        ) {
          if (!tweetText) {
            throw new Error("No tweet text provided");
          }

          const key = `${X_MEDIA_UPLOAD_STORAGE_KEY}_${questId}`;
          const tweetImage = localStorage.getItem(key);

          if (tweetImage) {
            localStorage.removeItem(key);
            const imageData = JSON.parse(tweetImage);
            const imageFile = base64ToFile(
              imageData.base64,
              imageData.filename,
            );

            const { imageId } = await platform.uploadXImageFile(imageFile);

            if (!imageId) {
              throw new Error("No image ID returned from X");
            }

            const res = await platform.validatePostTweetQuest(
              gameId,
              questId,
              accessToken,
              tweetText,
              imageId,
            );

            following = res.following;
          } else {
            const res = await platform.validatePostTweetQuest(
              gameId,
              questId,
              accessToken,
              tweetText,
            );

            following = res.following;
          }
        } else if (questKey === "x-v2" && platform.validateQuest) {
          const res = await platform.validateQuest(
            gameId,
            questId,
            accessToken,
          );
          following = res.following;
        } else if (
          questKey === "x-like-and-retweet-validation" &&
          platform.validateLikeAndRetweetQuest
        ) {
          const res = await platform.validateLikeAndRetweetQuest(
            gameId,
            questId,
            accessToken,
          );
          following = res.following;
        } else if (questKey === "steam") {
          const res = await platform.validateQuest(
            gameId,
            questId,
            accessToken,
          );
          following = res.following;
        } else {
          throw new Error("No quest completion function found");
        }

        if (following) {
          localStorage.setItem(questId, "true");
          setFollowing(true);
          setTitle(completionTitle);
          setMessage(
            `Head back to Immutable Play to finish your ${gameName} quests`,
          );

          let control: string;
          switch (questKey) {
            case "x-post-tweet-validation":
              control = "Tweeted";
              break;
            case "x-v2":
              control = "Followed";
              break;
            case "x-like-and-retweet-validation":
              control = "Liked and Retweeted";
              break;
            case "steam":
              control = "Wishlisted";
              break;
            default:
              control = "social-quest";
          }

          trackLegacyEvent({
            screen: "SocialLogin",
            userJourney: "Games",
            action: "Succeeded",
            control,
            controlType: "Effect",
            extras: {
              questId,
              platform: platform.name,
            },
          });
          trackLogin({
            action: Action.SUCCEEDED,
            extras: {
              path: "/",
              page_path: pathname || "",
              screen: "SocialLogin",
              userJourney: UserJourney.GAMES,
              control,
              controlType: "Effect",
              questId,
              platform: platform.name,
            },
          });
        } else if (questKey === "steam" && !following) {
          // Get the pending social quest to access the linkUrl for Steam
          const pendingSocialQuest: PendingSocialQuest | null = JSON.parse(
            localStorage.getItem(PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY) ??
              "{}",
          );
          const steamLinkUrl = pendingSocialQuest?.linkUrl;

          // Extract Steam ID from URL params when needed
          const steamId = callbackParams
            ? extractSteamIdFromParams(callbackParams)
            : null;
          const steamPrivacyUrl = steamId
            ? `https://steamcommunity.com/profiles/${steamId}/edit/settings`
            : null;

          setTitle("50% complete!");
          setMessage(
            <div className="text-center max-w-[420px] mx-auto px-4">
              <div className="space-y-4">
                <p>
                  1. Wishlist{" "}
                  {steamLinkUrl ? (
                    <a
                      href={steamLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                    >
                      {gameName}
                    </a>
                  ) : (
                    <strong>{gameName}</strong>
                  )}
                </p>

                <p>
                  2.{" "}
                  {steamPrivacyUrl ? (
                    <a
                      href={steamPrivacyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                    >
                      Visit your settings
                    </a>
                  ) : (
                    <strong>Visit your settings</strong>
                  )}
                  : Make sure <strong>Profile</strong> and{" "}
                  <strong>Game details</strong> are set to public
                </p>

                <p>
                  3. When complete,{" "}
                  <strong>
                    close this tab and return to the Immutable Play page
                  </strong>{" "}
                  for {gameName}
                </p>
              </div>
            </div>,
          );
        } else {
          setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
        }
      } catch (error) {
        const errorMessage = getCustomErrorContent(
          error instanceof AxiosError
            ? error.response?.data?.message
            : undefined,
          platform,
        );
        setTitle(errorMessage.heading);
        setMessage(errorMessage.body);
        if (error instanceof AxiosError && error.response?.data?.message) {
          localStorage.setItem(
            "social_quest_error",
            error.response.data.message,
          );
        }
        throw error;
      }
    },
    [getAccessToken, platform, pathname, trackLogin, callbackParams],
  );

  const handleLogin = useCallback(
    async (params: string) => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error("Unable to retrieve access token.");
          setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
          return;
        }

        await platform.login(accessToken, params);

        trackLegacyEvent({
          screen: "SocialLogin",
          userJourney: "Games",
          action: "Succeeded",
          control: "Screen",
          controlType: "Effect",
          extras: {
            platform: platform.name,
          },
        });
        trackLogin({
          action: Action.SUCCEEDED,
          extras: {
            path: "/",
            page_path: pathname || "",
            screen: "SocialLogin",
            userJourney: UserJourney.GAMES,
            control: "Screen",
            controlType: "Effect",
            platform: platform.name,
          },
        });

        const pendingSocialQuest: PendingSocialQuest | null = JSON.parse(
          localStorage.getItem(PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY) ?? "{}",
        );
        if (!pendingSocialQuest) {
          setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);

          throw new Error("No pending social quest found");
        }

        if (pendingSocialQuest.loginOnly) {
          localStorage.removeItem(PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY);
          setTitle(`You've connected your account on ${platform.name}`);
          setMessage("Head back to Immutable Play to continue");
          // we need to explicitly ignore X share validation quests from being completed without confirmation
        } else if (
          completeQuestOnSuccess &&
          pendingSocialQuest.questKey !== "x-share-validation"
        ) {
          await completeQuest(
            pendingSocialQuest.gameId,
            pendingSocialQuest.ruleId,
            pendingSocialQuest.gameName,
            pendingSocialQuest.questKey,
            pendingSocialQuest.tweetText,
          );
        } else {
          localStorage.removeItem(PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY);
          window.location.href = pendingSocialQuest.questLink;
        }
      } catch (error) {
        trackLegacyEvent({
          screen: "SocialLogin",
          userJourney: "Games",
          action: "Failed",
          control: "Screen",
          controlType: "Effect",
          extras: {
            platform: platform.name,
          },
        });
        trackLogin({
          action: Action.FAILED,
          extras: {
            path: "/",
            page_path: pathname || "",
            screen: "SocialLogin",
            userJourney: UserJourney.GAMES,
            control: "Screen",
            controlType: "Effect",
            platform: platform.name,
          },
        });

        console.error(`Error during ${platform.name} authentication:`, error);
        notifyError(error, errorName);

        if (error instanceof AxiosError && error.response?.data?.message) {
          const errorMessage = error.response.data.message;
          const socialErrors = [
            SOCIAL_LINK_ERROR_MESSAGES.SOCIAL_ACCOUNT_ALREADY_LINKED,
            SOCIAL_LINK_ERROR_MESSAGES.DIFFERENT_SOCIAL_ACCOUNT_ALREADY_LINKED,
            SOCIAL_LINK_ERROR_MESSAGES.RATE_LIMIT_ERROR,
          ];

          const modalErrorMessage = socialErrors.includes(errorMessage)
            ? errorMessage
            : SOCIAL_LINK_ERROR_MESSAGES.GENERIC_MODAL_ERROR;

          localStorage.setItem("social_auth_error", modalErrorMessage);
        }

        setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
      }
    },
    [
      getAccessToken,
      platform,
      errorName,
      completeQuest,
      completeQuestOnSuccess,
      pathname,
      trackLogin,
    ],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (platform.name === "Steam") {
      const params = urlParams.toString();
      if (!params) {
        console.error("Missing callback params in URL.");
        setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
        return;
      }
      setCallbackParams(params);
      return;
    }
    const code = urlParams.get("code");
    if (!code) {
      console.error("Missing 'code' parameter in URL.");
      setTitle(SOCIAL_LINK_ERROR_MESSAGES.GENERIC);
      return;
    }

    setCallbackParams(code);
  }, [platform.name]);

  useEffect(() => {
    if (
      callbackParams &&
      callbackParams !== previousCallbackParams &&
      !triggeredLoggedIn.current
    ) {
      triggeredLoggedIn.current = true;
      handleLogin(callbackParams);
    }
  }, [callbackParams, previousCallbackParams, handleLogin]);

  return (
    <RedirectLayout
      title={title}
      message={message}
      leftLogo={IMMUTABLE_LOGO}
      rightLogo={{
        src: `${appConfig.ASSET_BASE_URL}${platform.imageUrl}`,
        alt: `${platform.name} logo`,
      }}
      showSuccessState={following}
    />
  );
}
